import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createVNode as _createVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 1,
  class: "error-message"
}
const _hoisted_2 = {
  key: 2,
  class: "p-3"
}
const _hoisted_3 = { class: "title-4 text-primary-black" }
const _hoisted_4 = {
  key: 0,
  class: "text-muted mt-2"
}

import { onMounted, ComputedRef, computed, ref } from 'vue'

  // Composables
  import { useBookingStatus } from '@/composables/useBookingStatus'
  import { useCompanyChecks } from '@/composables/useCompanyChecks'

  // Store
  import { useStore } from '@/store'

  // Types
import { BookingSummary, OptionSummary } from '@/services/BookingService.interface'
import { Document, DocumentType } from '@/services/DocumentsService.interface'
import { PrestType } from '@/config'
import { getInsuranceDocumentLink } from '@/config/insuranceDocumentLinks'

  // Components
  import Card from '@/components/UI/Card.vue'
  import DocumentComp from './Document.vue'
  import Skeleton from './DocumentSectionSkeleton.vue'

  // Translations
  import { useI18n } from 'vue-i18n'
  interface Props {
    booking: BookingSummary | null
  }

  
export default /*@__PURE__*/_defineComponent({
  __name: 'DocumentsSection',
  props: {
    booking: {}
  },
  setup(__props: any) {

  // Vue
  const { t, locale } = useI18n()

  const store = useStore()

  // Store documents as an array instead of a record
  const baseDocuments = ref<Document[]>([])

  // Props
  const props = __props

  // Computed
  const { isReservationStatus, isPastStatus } = useBookingStatus(props.booking)
  const { isCOSOG } = useCompanyChecks()

  const loading: ComputedRef<boolean> = computed(() => {
    return store.getters['document/isLoadingAny']
  })

  const documents: ComputedRef<Document[]> = computed(() => {
    if (props.booking) {
      return store.getters['document/getDocuments'][props.booking.reference]
    }

    return []
  })

  const error = computed(() =>
    store.getters['document/getError']('getDocuments')
  )

  // Methods

  // Generate insurance documents from booking insurance services
  const generateInsuranceDocuments = (): Document[] => {
    if (!props.booking) return []

    const insuranceDocuments: Document[] = []
    
    // Find insurance services in booking options
    const insuranceOptions = props.booking.options.filter(
      (option: OptionSummary) => option.prestTypeId === PrestType.Insurance
    )

    insuranceOptions.forEach((insuranceOption: OptionSummary) => {
      let documentType: DocumentType
      
      // Determine insurance type based on the service name
      if (insuranceOption.name?.toLowerCase().includes('skieur')) {
        documentType = DocumentType.InsuranceSkieur
      } else if (insuranceOption.name?.toLowerCase().includes('annulation') || 
                 insuranceOption.name?.toLowerCase().includes('zen')) {
        documentType = DocumentType.InsuranceAnnulation
      } else {
        // Default to annulation for unknown insurance types
        documentType = DocumentType.InsuranceAnnulation
      }

      // Get the file link from static configuration based on document type and locale
      // TODO The link should come from catlog endpoint 
      const fileLink = getInsuranceDocumentLink(documentType, locale.value)

      // Create insurance document
      insuranceDocuments.push({
        documentType,
        label: insuranceOption.name || t(`documents.${documentType}`),
        fileLink,
        bookingReference: props.booking?.reference || '',
        isActive: true,
        includeInVoucher: false
      })
    })

    return insuranceDocuments
  }

  const BOOKING_CONFIRMATION_TYPES = new Set([
    DocumentType.BookingConfirmation,
    'booking-confirmation-flight',
    'booking-confirmation-train',
  ])

  const createPlaceholderDocument = (documentType: DocumentType): Document => ({
    documentType,
    label: t(`documents.${documentType}`),
    fileLink: '',
    bookingReference: props.booking?.reference || '',
    isActive: true,
    includeInVoucher: false,
  })

  // Ensure vouchers and invoice always appear with availability messaging when not yet returned by the API
  const ensureStandardPlaceholders = (documents: Document[]): Document[] => {
    const result = [...documents]

    if (!result.some(doc => doc.documentType === DocumentType.Vouchers)) {
      const placeholder = createPlaceholderDocument(DocumentType.Vouchers)
      const bookingConfirmationIndex = result.findIndex(doc =>
        BOOKING_CONFIRMATION_TYPES.has(doc.documentType)
      )
      if (bookingConfirmationIndex >= 0) {
        result.splice(bookingConfirmationIndex + 1, 0, placeholder)
      } else {
        result.push(placeholder)
      }
    }

    if (!result.some(doc => doc.documentType === DocumentType.Invoice)) {
      const placeholder = createPlaceholderDocument(DocumentType.Invoice)
      const vouchersIndex = result.findIndex(doc => doc.documentType === DocumentType.Vouchers)
      if (vouchersIndex >= 0) {
        result.splice(vouchersIndex + 1, 0, placeholder)
      } else {
        result.push(placeholder)
      }
    }

    return result
  }

  // Update the base documents with values from the fetched documents and insurance documents
  const enrichBaseDocuments = (fetchedDocs: Document[]): void => {
    if (!Array.isArray(fetchedDocs)) {
      console.error('Fetched documents are not an array:', fetchedDocs)
      return
    }
    
    const insuranceDocuments = generateInsuranceDocuments()
    let allDocuments = ensureStandardPlaceholders(fetchedDocs)
    allDocuments = [...allDocuments, ...insuranceDocuments]
    
        // Apply COGOS-specific modifications. Business Requirement
        if (isCOSOG.value) {
          allDocuments = allDocuments.map(document => {
            if (document.documentType === DocumentType.InsuranceAnnulation) {
              // Use the specific COGOS link from configuration
              const cogosLink = getInsuranceDocumentLink(DocumentType.InsuranceAnnulation, 'fr-FR-CSE-COSOG')
              return {
                ...document,
                fileLink: cogosLink,
                documentType: 'insurance-annulation-cosog' as DocumentType
              }
            }
            return document
          })
        }

    baseDocuments.value = allDocuments
  }
  onMounted(async () => {
    if (props.booking && (isReservationStatus.value || isPastStatus.value)) {
      // Fetch documents using both primary reference and BO reference (if available)
      const primaryReference = props.booking.reference
      const boReference = props.booking.extReferenceBO
      
      // Pass both references to the store action
      await store.dispatch('document/getDocuments', {
        bookingReference: primaryReference,
        boReference: boReference && boReference.trim() !== '' && boReference !== primaryReference
          ? boReference
          : undefined
      })

      enrichBaseDocuments(documents.value || [])
    }
  })

return (_ctx: any,_cache: any) => {
  return (_unref(isReservationStatus) || _unref(isPastStatus))
    ? (_openBlock(), _createBlock(Card, {
        key: 0,
        "additional-classes": "px-3"
      }, {
        default: _withCtx(() => [
          (loading.value)
            ? (_openBlock(), _createBlock(Skeleton, { key: 0 }))
            : (error.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_1, _toDisplayString(_unref(t)('errorMessages.getDocuments')), 1))
              : (_openBlock(), _createElementBlock("div", _hoisted_2, [
                  _createElementVNode("p", _hoisted_3, _toDisplayString(_unref(t)('documents.title')), 1),
                  (baseDocuments.value.length === 0)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(_unref(t)('documents.noDocuments')), 1))
                    : _createCommentVNode("", true),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(baseDocuments.value, (document, index) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: document.id ?? document.fileLink ?? `${document.documentType}-${document.bookingReference}-${index}`
                    }, [
                      _cache[0] || (_cache[0] = _createElementVNode("div", { class: "seperator" }, null, -1)),
                      _createVNode(DocumentComp, { document: document }, null, 8, ["document"])
                    ]))
                  }), 128))
                ]))
        ]),
        _: 1
      }))
    : _createCommentVNode("", true)
}
}

})